import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "@shared/utils/internationalization";
import { getItemPrice } from "@shared/modules/financials/utils/price-utils";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import AllInPricing from "@shared/modules/financials/components/AllInPricing/AllInPricing.web";
import CurrencyFormat from "@ui-kit/data-display/CurrencyFormat/CurrencyFormat.web";
import Button from "@ui-kit/form/Button/Button.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { TicketItemGroupType } from "@shared/modules/item-groups/types/ticket-item-group-type";
import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import useAssignedSeatingHelpers from "@shared/hooks/seating/useAssignedSeatingHelpers/useAssignedSeatingHelpers";
import { ASSIGNED_SEATING, enums } from "@shared/constants";
import {
  MapPermissionsType,
  PublicBasedTTSeatPermissionsRenewalPermissionsSerializer,
} from "@shared/modules/seating/types/seating-types";
import usePublicBasedTTSeatPermissionsRenewalPermissionsService from "@shared/modules/seating/services/usePublicBasedTTSeatPermissionsRenewalPermissionsService";
import { ItemBodyProps } from "./ItemBody.props";
import ItemVisibility from "../ItemVisibility/ItemVisibility.web";
import ItemSelectModal from "../../ItemSelectModal/ItemSelectModal.web";

const ItemBody = ({
  formattedData,
  itemObj,
  seatingEventName,
  venueData,
  isLimitedVisibility,
  isAccessible,
}: ItemBodyProps) => {
  const { t } = useTranslation("seating");

  const {
    itemGroups,
    getSubItemGroups,
    purchaseSourcePlatform,
    paymentType,
    areAllRenewalItemGroups,
  } = useBasketService();

  const [showPackageDetails, setShowPackageDetails] = useState(false);

  // use the full itemgroup object from the basket to get item subgroup info
  const itemGroupInBasket = useMemo(
    () => itemGroups?.find((ig) => ig.id === formattedData.itemGroup?.id),
    [itemGroups, formattedData.itemGroup?.id]
  );
  // subgroups for item package display
  const itemSubGroups = useMemo(
    () => getSubItemGroups(itemGroupInBasket ?? {}),
    [itemGroupInBasket, getSubItemGroups]
  );

  const { seatingEventData, seatingServiceState, seatingItemArray } =
    useSeatingState();
  const {
    itemSelectModalOpen,
    ticketAuthArray,
    resolveItemSelect,
    rejectItemSelect,
    updateTicketAuthArray,
    handleSeatSelect,
  } = useAssignedSeatingHelpers();

  // Get the current seat from the cart permission
  const currentSeat = seatingServiceState.getCartPermission(
    formattedData.permissionId
  )?.seat;

  // State for storing membership level permissions
  const [seatPermissions, setSeatPermissions] = useState<
    Array<MapPermissionsType>
  >([]);

  const { useGetPermissions } =
    usePublicBasedTTSeatPermissionsRenewalPermissionsService();
  const params = useMemo(
    () => ({
      seat: Number(formattedData.seat?.id),
      membership_group: formattedData.itemGroup?.membership_group,
    }),
    [formattedData.seat?.id, formattedData.itemGroup?.membership_group]
  );

  const { data: membershipLevels } = useGetPermissions({
    params,
    options: {
      enabled: Boolean(
        currentSeat?.id &&
          formattedData.itemGroup?.renewal_member &&
          formattedData.itemGroup?.item_type ===
            enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id
      ),
    },
  });

  // Keep only membership levels present in seatingItemArray to enforce visibility rules.
  const filteredMembershipLevels = useMemo(() => {
    if (!membershipLevels?.membership_levels || !seatingItemArray) {
      return { membership_levels: [] };
    }

    const availableMembershipLevels = membershipLevels.membership_levels.filter(
      (membershipLevel) =>
        // Check if any item in seatingItemArray has the same ID
        seatingItemArray.some((item) => item.id === membershipLevel.id)
    );

    return {
      membership_levels: availableMembershipLevels,
    };
  }, [membershipLevels, seatingItemArray]);

  useEffect(() => {
    if (!currentSeat?.id) return;

    const permissions: MapPermissionsType[] = [];

    if (filteredMembershipLevels) {
      if (
        filteredMembershipLevels.membership_levels &&
        filteredMembershipLevels.membership_levels.length > 0
      ) {
        filteredMembershipLevels.membership_levels.forEach(
          (
            membershipLevel: PublicBasedTTSeatPermissionsRenewalPermissionsSerializer["membership_levels"][number]
          ) => {
            permissions.push({
              seatingItemPermissionId: membershipLevel.seat_permissions[0],
              seatingItemTypeId: membershipLevel.id,
            });
          }
        );
      }
      setSeatPermissions(permissions);
    }
  }, [currentSeat?.id, membershipLevels]);

  const itemsOnSeat = useMemo(() => {
    if (!currentSeat) return undefined;

    return {
      $data: currentSeat,
      $name: ASSIGNED_SEATING.SEAT.ID,
      $permissions: seatPermissions.length > 0 ? seatPermissions : [],
    };
  }, [currentSeat, seatPermissions, formattedData]);

  return (
    <>
      {areAllRenewalItemGroups ? (
        <>
          <Text fontSize="sm">
            {formattedData.itemGroup?.renewal_member_info?.username}
          </Text>
          <Text fontSize="md" fontWeight="bold">
            {
              formattedData.itemGroup?.renewal_member_info
                ?.membership_group_name
            }
          </Text>
          <Text mb="2xs" fontSize="sm">
            {itemObj?.name}
          </Text>
        </>
      ) : (
        <Text mb="2xs" fontSize="sm">
          {`${itemObj?.name} - ${seatingEventName}`}
        </Text>
      )}

      {/* Accessibility / Limited Visibility Text */}
      <ItemVisibility
        isLimitedVisibility={isLimitedVisibility}
        isAccessible={isAccessible}
      />

      {/* Price Breakdown Text */}

      {itemObj?.pay_what_you_can_configuration && itemGroupInBasket?.price ? (
        <>
          <Text mb="2xs" fontSize="sm" fontWeight="bold">
            <CurrencyFormat
              value={getItemPrice(
                itemGroupInBasket?.price,
                false,
                itemObj?.fees_pricing_info,
                paymentType,
                purchaseSourcePlatform
              )}
              displayType="text"
              currency={venueData.currency}
              includeSuffix
            />
          </Text>
        </>
      ) : (
        <>
          <Flex justifyContent="space-between" alignItems="center">
            <Text fontSize="sm" fontWeight="bold">
              <CurrencyFormat
                value={getItemPrice(
                  itemObj?.price?.toString(),
                  Boolean(venueData.all_in_pricing_is_enabled),
                  itemObj?.fees_pricing_info,
                  paymentType,
                  purchaseSourcePlatform
                )}
                displayType="text"
                currency={venueData.currency}
                includeSuffix
              />
            </Text>
            {/* Only show the edit level button if the item group has a renewal member, the item type is a membership, and there are multiple permissions on the seat */}
            {formattedData.itemGroup?.renewal_member &&
              formattedData.itemGroup?.item_type ===
                enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id &&
              (itemsOnSeat?.$permissions?.length || 0) > 1 && (
                <Button
                  data-testid="edit-level-button"
                  colorPalette="font.dark"
                  variant="link"
                  size="sm"
                  onClick={() => {
                    if (
                      !itemsOnSeat?.$data ||
                      !itemsOnSeat?.$name ||
                      !itemsOnSeat?.$permissions
                    )
                      return;
                    handleSeatSelect({
                      $data: itemsOnSeat?.$data,
                      $name: itemsOnSeat?.$name,
                      $permissions: itemsOnSeat?.$permissions,
                      isModifyLevel: true,
                    });
                  }}
                >
                  {t("Edit Level")}
                </Button>
              )}
          </Flex>
          <Flex color="gray.600" fontSize="sm" mt="2xs">
            <AllInPricing
              hasAllInPricing={Boolean(venueData.all_in_pricing_is_enabled)}
              feesPricingInfo={itemObj?.fees_pricing_info}
              price={itemObj?.price?.toString()}
              currency={venueData.currency}
              purchaseSourcePlatform={purchaseSourcePlatform}
              paymentType={paymentType}
            />
          </Flex>
        </>
      )}

      {/* Package Breakdown */}
      <Flex
        mt="2xs"
        fontSize="xs"
        direction="column"
        alignContent="flex-start"
        hidden={!itemSubGroups || itemSubGroups?.length === 0}
      >
        <Text>
          {t("Includes")}
          {` ${itemSubGroups.length} `}
          {itemSubGroups.length === 1 ? t("Item") : t("Items")}
          {" - "}
          <Button
            data-testid={`item-body-view-details-${itemObj?.id}`}
            width="fit-content"
            colorPalette="tertiary"
            size="xs"
            variant="link"
            onClick={() => setShowPackageDetails(!showPackageDetails)}
          >
            <Text textDecoration="underline">{t("View Details")}</Text>
          </Button>
        </Text>
        {showPackageDetails &&
          itemSubGroups?.map((childGroup: TicketItemGroupType) => (
            <Text key={childGroup.id} color="gray.600">
              {childGroup.description}
            </Text>
          ))}
      </Flex>
      {formattedData.itemGroup?.renewal_member && // we only want to show the edit level button if the item group has a renewal member
        seatingEventData &&
        itemsOnSeat &&
        itemsOnSeat.$data && (
          <ItemSelectModal
            isOpen={itemSelectModalOpen}
            onClose={rejectItemSelect}
            resolve={resolveItemSelect}
            seatingEvent={seatingEventData}
            venueData={venueData}
            itemsOnSeat={itemsOnSeat}
            ticketAuthArray={ticketAuthArray}
            updateTicketAuthArray={updateTicketAuthArray}
          />
        )}
    </>
  );
};

export default ItemBody;
